import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  fetchPacingHolds,
  createPacingHold,
  cancelPacingHold,
  fetchPacingGatingRules,
} from '@src/api/actions';
import { fetchVerticalsForClient } from '@src/api/actions/verticals';
import { fetchClients } from '@src/api/actions/clients';
import { fetchClientTerritories } from '@src/api/actions/clientTerritories';
import { Autocomplete } from '@mui/material';

const TIER_OPTIONS = [
  { value: 'slow_down', label: 'Slow Down', blurb: 'We\'ll throttle activity in the selected scope.' },
  { value: 'minimal_spend', label: 'Minimal Spend', blurb: 'We\'ll keep activity at a minimum through the end date.' },
];

const SCOPE_OPTIONS = [
  { value: 'overall', label: 'Whole account' },
  { value: 'vertical', label: 'Specific vertical' },
  { value: 'territory', label: 'Specific territory' },
];

const REASON_OPTIONS = [
  { value: 'calendars_full', label: 'Calendars full' },
  { value: 'team_capacity', label: 'Team at capacity' },
  { value: 'promotion_ended', label: 'Promotion ended' },
  { value: 'lead_quality', label: 'Want to slow lead intake' },
  { value: 'other', label: 'Other' },
];

const STATUS_COLOR = {
  active: 'warning',
  scheduled: 'info',
  completed: 'default',
  canceled: 'default',
};

function formatDate(s) {
  if (!s) return '-';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysISO(baseISO, n) {
  const d = new Date(baseISO);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function PacingControlPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClientId = searchParams.get('client_id');
  const clientId = queryClientId ? Number(queryClientId) : null;

  const [clients, setClients] = useState([]);
  const [holds, setHolds] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);

  // Load clients + rules once. Verticals + territories reload per client (filtered to
  // the client's actual ClientZipVertical mappings + their configured territories).
  useEffect(() => {
    Promise.all([fetchClients(), fetchPacingGatingRules()])
      .then(([clientsRes, rulesRes]) => {
        setClients(clientsRes.data?.results || clientsRes.data || []);
        setRules(rulesRes.data || null);
      })
      .catch((err) => setError(err?.message || 'Failed to load metadata.'));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setVerticals([]);
      setTerritories([]);
      return;
    }
    Promise.all([
      fetchVerticalsForClient(clientId),
      fetchClientTerritories(clientId),
    ])
      .then(([vertRes, terrRes]) => {
        setVerticals(vertRes.data?.results || vertRes.data || []);
        setTerritories(terrRes.data?.results || terrRes.data || []);
      })
      .catch(() => {
        setVerticals([]);
        setTerritories([]);
      });
  }, [clientId]);

  const load = useCallback(() => {
    if (!clientId) {
      setHolds([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPacingHolds({ client_id: clientId })
      .then((res) => setHolds(res.data?.results || res.data || []))
      .catch((err) => setError(err?.message || 'Failed to load pacing data.'))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId) || null,
    [clients, clientId],
  );

  const handleClientChange = (newClient) => {
    const next = new URLSearchParams(searchParams);
    if (newClient?.id) {
      next.set('client_id', String(newClient.id));
    } else {
      next.delete('client_id');
    }
    setSearchParams(next);
  };

  const active = useMemo(() => holds.filter((h) => h.is_currently_active), [holds]);
  const scheduled = useMemo(() => holds.filter((h) => h.status === 'scheduled' && !h.is_currently_active), [holds]);
  const history = useMemo(() => holds.filter((h) => ['completed', 'canceled'].includes(h.status)), [holds]);

  const handleCancel = (id) => {
    if (!window.confirm('Cancel this pacing adjustment? Spend will return to normal pace.')) return;
    cancelPacingHold(id).then(() => load()).catch((err) => setError(err?.message || 'Cancel failed.'));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="h5">Pacing Control</Typography>
            <Typography variant="body2" color="text.secondary">
              Slow down market activity for a few days when capacity is constrained. Budgets stay the same — pace catches up after the hold ends.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => setRequestOpen(true)}
            disabled={loading || !clientId}
          >
            Request Slowdown
          </Button>
        </Stack>

        <Autocomplete
          options={clients}
          getOptionLabel={(opt) => opt?.name || ''}
          value={selectedClient}
          onChange={(_, val) => handleClientChange(val)}
          sx={{ maxWidth: 380 }}
          renderInput={(params) => <TextField {...params} label="Client" size="small" />}
          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
        />

        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

        {!clientId ? (
          <Alert severity="info">Select a client to view their pacing holds.</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Card>
              <CardHeader title={`Active (${active.length})`} subheader="In effect right now" />
              <CardContent sx={{ pt: 0 }}>
                {active.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No active pacing adjustments.</Typography>
                ) : (
                  <HoldList holds={active} onCancel={handleCancel} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader title={`Scheduled (${scheduled.length})`} subheader="Kicks in on a future date" />
              <CardContent sx={{ pt: 0 }}>
                {scheduled.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">None scheduled.</Typography>
                ) : (
                  <HoldList holds={scheduled} onCancel={handleCancel} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader title={`Recent (last 90 days)`} subheader="Completed or canceled holds" />
              <CardContent sx={{ pt: 0 }}>
                {history.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No history yet.</Typography>
                ) : (
                  <HoldList holds={history.slice(0, 25)} historical />
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Stack>

      <RequestDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        onCreated={() => { setRequestOpen(false); load(); }}
        clientId={clientId}
        verticals={verticals}
        territories={territories}
        rules={rules}
      />
    </Box>
  );
}

function HoldList({ holds, onCancel, historical = false }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Scope</TableCell>
            <TableCell>Tier</TableCell>
            <TableCell>Dates</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Status</TableCell>
            {!historical && <TableCell align="right">Actions</TableCell>}
            {historical && <TableCell align="right">Impact</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {holds.map((h) => (
            <TableRow key={h.id}>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{h.scope_label || h.scope_type_label}</Typography>
                <Typography variant="caption" color="text.secondary">{h.scope_type_label}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={h.tier_label}
                  size="small"
                  color={h.tier === 'minimal_spend' ? 'error' : 'warning'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatDate(h.start_date)} → {formatDate(h.end_date)}</Typography>
                <Typography variant="caption" color="text.secondary">{h.duration_days}d</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{h.reason_code || '—'}</Typography>
                {h.notes && <Typography variant="caption" color="text.secondary">{h.notes}</Typography>}
              </TableCell>
              <TableCell>
                <Chip label={h.status} size="small" color={STATUS_COLOR[h.status] || 'default'} />
              </TableCell>
              {!historical && (
                <TableCell align="right">
                  <Button size="small" color="error" onClick={() => onCancel(h.id)}>Cancel</Button>
                </TableCell>
              )}
              {historical && (
                <TableCell align="right">
                  {h.measured_reduction_pct != null
                    ? <Typography variant="body2">−{Number(h.measured_reduction_pct).toFixed(0)}%</Typography>
                    : <Typography variant="caption" color="text.secondary">—</Typography>
                  }
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RequestDialog({ open, onClose, onCreated, clientId, verticals, territories, rules }) {
  const today = todayISO();
  const [scopeType, setScopeType] = useState('overall');
  const [scopeId, setScopeId] = useState('');
  const [tier, setTier] = useState('slow_down');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(addDaysISO(today, 2));
  const [reasonCode, setReasonCode] = useState('calendars_full');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [violations, setViolations] = useState([]);

  useEffect(() => {
    if (open) {
      setScopeType('overall');
      setScopeId('');
      setTier('slow_down');
      setStartDate(today);
      setEndDate(addDaysISO(today, 2));
      setReasonCode('calendars_full');
      setNotes('');
      setValidationError('');
      setViolations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    setSubmitting(true);
    setValidationError('');
    setViolations([]);
    const payload = {
      client: clientId,
      scope_type: scopeType,
      scope_id: scopeType === 'overall' ? null : scopeId,
      tier,
      start_date: startDate,
      end_date: endDate,
      reason_code: reasonCode,
      notes,
    };
    createPacingHold(payload)
      .then(() => onCreated())
      .catch((err) => {
        const data = err?.response?.data;
        setValidationError(data?.detail || err?.message || 'Request failed.');
        setViolations(data?.violations || []);
      })
      .finally(() => setSubmitting(false));
  };

  const tierBlurb = TIER_OPTIONS.find((t) => t.value === tier)?.blurb || '';
  const scopeNeedsId = scopeType !== 'overall';
  const submitDisabled = submitting || (scopeNeedsId && !scopeId) || !startDate || !endDate;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request a Slowdown</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Scope</InputLabel>
            <Select value={scopeType} label="Scope" onChange={(e) => { setScopeType(e.target.value); setScopeId(''); }}>
              {SCOPE_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {scopeType === 'vertical' && (
            <FormControl fullWidth size="small" disabled={verticals.length === 0}>
              <InputLabel>Vertical</InputLabel>
              <Select value={scopeId} label="Vertical" onChange={(e) => setScopeId(e.target.value)}>
                {verticals.map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.vertical || v.name}</MenuItem>
                ))}
              </Select>
              {verticals.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  No active verticals configured for this client.
                </Typography>
              )}
            </FormControl>
          )}

          {scopeType === 'territory' && (
            <FormControl fullWidth size="small" disabled={territories.length === 0}>
              <InputLabel>Territory</InputLabel>
              <Select value={scopeId} label="Territory" onChange={(e) => setScopeId(e.target.value)}>
                {territories.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
              {territories.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  No territories configured for this client.
                </Typography>
              )}
            </FormControl>
          )}

          <FormControl fullWidth size="small">
            <InputLabel>How much?</InputLabel>
            <Select value={tier} label="How much?" onChange={(e) => setTier(e.target.value)}>
              {TIER_OPTIONS.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">{tierBlurb}</Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth size="small" label="Start" type="date"
              value={startDate} onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth size="small" label="End" type="date"
              value={endDate} onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          {rules && (
            <Typography variant="caption" color="text.secondary">
              Duration must be {rules.min_duration_days}–{rules.max_duration_days} days.
              Cooldown of {rules.cooldown_days} days between holds on the same scope.
            </Typography>
          )}

          <FormControl fullWidth size="small">
            <InputLabel>Reason</InputLabel>
            <Select value={reasonCode} label="Reason" onChange={(e) => setReasonCode(e.target.value)}>
              {REASON_OPTIONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth size="small" multiline rows={2} label="Notes (optional)"
            value={notes} onChange={(e) => setNotes(e.target.value)}
          />

          {validationError && (
            <Alert severity="error">
              {validationError}
              {violations.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption">Violations:</Typography>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {violations.map((v) => <li key={v}><Typography variant="caption">{v.replace(/_/g, ' ')}</Typography></li>)}
                  </ul>
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitDisabled}>
          {submitting ? <CircularProgress size={18} /> : 'Request Slowdown'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PacingControlPage;
